import React, { useState, useRef, useEffect } from 'react';
import {
    MediaPlayer,
    MediaProvider,
    Menu,
    RadioGroup,
    Tooltip,
    usePlaybackRateOptions,
    type MediaPlayerInstance,
    type VideoSrc,
} from '@vidstack/react';
import { DefaultVideoLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';

import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import api from '../../utils/axios';
import { TestPlanPopover, ScenarioItem, BatchItem } from './TestPlanPopover';

// Identifies a recording for the share panel (hosted at page level in ChatPage).
export interface TestRecordingShareInfo {
    recordingId: string;
    projectId: string;
    title: string;
}

interface Recording {
    path: string;
}

export type { ScenarioItem, BatchItem };

interface TestRecordingViewerProps {
    recording: Recording;
    projectId: string;
    messageId: string;
    recordingId?: string | null;  // stable id; share/download are shown only when present
    className?: string;
    isLive?: boolean;
    currentAction?: string;
    currentScenario?: string | null;  // Current test scenario being tested
    batchName?: string | null;  // Name of the batch (used as video title)
    liveFrame?: string | null;  // Base64 frame data from WebSocket
    isBatchInitializing?: boolean;
    scenarios?: ScenarioItem[];  // List of scenarios for current batch
    testPlan?: BatchItem[];  // Full test plan with all batches
    currentBatchIndex?: number;  // Index of the current batch being executed
    onShare?: (info: TestRecordingShareInfo) => void;  // Open the (page-level) share panel
}

interface VideoPlayerContentProps {
    videoUrl: string;
    mimeType: string;
    playerRef: React.RefObject<MediaPlayerInstance>;
    duration?: number;
    autoPlay?: boolean;
}

// YouTube-style standalone playback-speed control that lives in the control bar
const PLAYBACK_RATES = [{ label: "1x", rate: 1.0 }, { label: "2x", rate: 2.0 }, { label: "4x", rate: 4.0 }];
const PlaybackRateMenu: React.FC = () => {
    const options = usePlaybackRateOptions({ rates: PLAYBACK_RATES });
    const currentRate = options.selectedValue ?? '1';

    return (
        <Menu.Root className="vds-menu">
            <Tooltip.Root>
                <Tooltip.Trigger asChild>
                    <Menu.Button className="vds-menu-button vds-button" aria-label="Playback Speed">
                        <span className="vds-menu-button-label inline-flex items-center gap-0.5" style={{ fontSize: '14px', fontWeight: 500 }}>
                            <Icon name={IconName.PlaybackSpeed} size="sm" inheritColor />
                            {currentRate}x
                        </span>
                    </Menu.Button>
                </Tooltip.Trigger>
                <Tooltip.Content className="vds-tooltip-content" placement="top">
                    Playback Speed
                </Tooltip.Content>
            </Tooltip.Root>
            <Menu.Content
                className="vds-menu-items [--media-menu-min-width:0px] [--media-menu-bg:transparent] [--media-menu-border:none] [--media-menu-padding:0px] [--media-menu-box-shadow:none] [--media-menu-filter:none] [--media-menu-border-radius:0px]"
                placement="top end"
            >
                <RadioGroup.Root
                    className="min-w-[92px] flex flex-col gap-0.5 rounded-lg border border-border-secondary bg-bg-primary p-1 shadow-lg"
                    value={options.selectedValue}
                >
                    {options.map(({ label, value, select, selected }) => (
                        <RadioGroup.Item
                            className={`flex w-full items-center justify-start rounded-md px-3 py-1.5 min-h-8 text-[13px] font-medium cursor-pointer transition-colors outline-none ${
                                selected
                                    ? 'bg-bg-brand-primary-alt text-text-brand-primary'
                                    : 'text-text-secondary hover:bg-bg-brand-primary-alt hover:text-text-brand-primary'
                            }`}
                            value={value}
                            onSelect={select}
                            key={value}
                        >
                            {label}
                        </RadioGroup.Item>
                    ))}
                </RadioGroup.Root>
            </Menu.Content>
        </Menu.Root>
    );
};

export const getMimeTypeFromFilename = (name: string): string => {
    const ext = name.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
        'webm': 'video/webm',
        'mp4': 'video/mp4',
        'mkv': 'video/x-matroska',
        'avi': 'video/x-msvideo',
        'mov': 'video/quicktime',
        'ogg': 'video/ogg',
    };
    return mimeTypes[ext || ''] || 'video/webm';
};

// Derive a WebM/video duration from a blob (handles WebM files that lack duration metadata).
export const getBlobDuration = async (blob: Blob): Promise<number | undefined> => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        const blobUrl = URL.createObjectURL(blob);

        video.onloadedmetadata = () => {
            URL.revokeObjectURL(blobUrl);
            if (Number.isFinite(video.duration)) {
                resolve(video.duration);
            } else {
                // WebM duration workaround: seek to end to get actual duration
                video.currentTime = Number.MAX_SAFE_INTEGER;
            }
        };

        video.ontimeupdate = () => {
            URL.revokeObjectURL(blobUrl);
            video.ontimeupdate = null;
            resolve(video.duration);
        };

        video.onerror = () => {
            URL.revokeObjectURL(blobUrl);
            resolve(undefined);
        };

        // Timeout fallback
        setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            resolve(undefined);
        }, 5000);

        video.src = blobUrl;
    });
};

export const VideoPlayerContent: React.FC<VideoPlayerContentProps> = ({ videoUrl, mimeType, playerRef, duration, autoPlay = false }) => {
    const videoSource: VideoSrc = { src: videoUrl, type: mimeType as VideoSrc['type'] };

    return (
        <div className="test-recording-player relative rounded-bg-v2-sm overflow-hidden">
            <MediaPlayer
                ref={playerRef}
                src={videoSource}
                streamType="on-demand"
                viewType="video"
                playsInline
                muted={true}
                autoPlay={autoPlay}
                duration={duration}
                className="w-full aspect-video"
                fullscreenOrientation="none"
            >
                <MediaProvider />
                <DefaultVideoLayout
                    icons={defaultLayoutIcons}
                    smallLayoutWhen={false}
                    slots={{
                        beforePlayButton: null,
                        afterPlayButton: null,
                        pipButton: null,
                        captionButton: null,
                        settingsMenu: <PlaybackRateMenu />,
                        airPlayButton: null,
                        googleCastButton: null,
                        seekBackwardButton: null,
                        seekForwardButton: null,
                        volumeSlider: null,
                        muteButton: null,
                    }}
                />
            </MediaPlayer>
        </div>
    );
};

export const TestRecordingViewer: React.FC<TestRecordingViewerProps> = ({
    recording,
    projectId,
    messageId,
    recordingId = null,
    className = '',
    isLive = false,
    currentAction = '',
    currentScenario = null,
    batchName = null,
    liveFrame = null,
    isBatchInitializing = false,
    scenarios = [],
    testPlan = [],
    currentBatchIndex = 0,
    onShare,
}) => {
    const playerRef = useRef<MediaPlayerInstance>(null);
    const liveContainerRef = useRef<HTMLDivElement>(null);
    const infoButtonRef = useRef<HTMLButtonElement>(null);
    
    const [isExpanded, setIsExpanded] = useState(isLive); // Auto-expand when live
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [mimeType, setMimeType] = useState<string>('video/webm');
    const [loadingVideo, setLoadingVideo] = useState(false);
    const [videoDuration, setVideoDuration] = useState<number | undefined>(undefined);
    const [showScenariosPopover, setShowScenariosPopover] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const pathParts = recording.path.split('/');
    const filename = pathParts.pop() || '';
    const recordingMessageId = pathParts.length >= 2 ? pathParts[pathParts.length - 1] : messageId;
    const recordingProjectId = pathParts.length >= 3 ? pathParts[pathParts.length - 2] : projectId;

    // Load video when expanded (only if not already loaded, not live, and not initializing)
    useEffect(() => {
        if (isExpanded && !isLive && !isBatchInitializing && !videoUrl && !loadingVideo) {
            loadVideo();
        }
    }, [isExpanded, isLive, isBatchInitializing, videoUrl, loadingVideo]);

    // Cleanup blob URL only on unmount - use ref to track latest URL without causing effect reruns
    const videoUrlRef = useRef<string | null>(null);
    useEffect(() => {
        videoUrlRef.current = videoUrl;
    }, [videoUrl]);
    
    useEffect(() => {
        return () => {
            // Only revoke on actual component unmount
            if (videoUrlRef.current) {
                console.log('[TestRecordingViewer] Unmounting, revoking blob URL:', videoUrlRef.current);
                URL.revokeObjectURL(videoUrlRef.current);
            }
        };
    }, []); // Empty deps = only runs cleanup on unmount

    // Auto-expand when live streaming starts or batch is initializing
    useEffect(() => {
        if (isLive || isBatchInitializing) {
            setIsExpanded(true);
        }
    }, [isLive, isBatchInitializing]);

    // Track fullscreen state changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === liveContainerRef.current);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Handle opening/closing popover
    const handleInfoClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowScenariosPopover(!showScenariosPopover);
    };

    // Download the completed recording to the user's device
    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (downloading || !recordingId) return;
        setDownloading(true);
        try {
            const response = await api.get(
                `/browser-stream/recordings/${encodeURIComponent(recordingProjectId)}/${encodeURIComponent(recordingId)}/download`,
                { responseType: 'blob' }
            );
            const blob = response.data as Blob;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('[TestRecordingViewer] Failed to download recording:', err);
            setError('Failed to download recording');
        } finally {
            setDownloading(false);
        }
    };

    // Delegate to the page-level share panel (hosted in ChatPage)
    const handleShareClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!recordingId || !onShare) return;
        onShare({
            recordingId,
            projectId: recordingProjectId,
            title: getDisplayTitle(),
        });
    };

    const toggleFullscreen = async () => {
        if (!liveContainerRef.current) return;
        
        if (document.fullscreenElement) {
            await document.exitFullscreen();
        } else {
            await liveContainerRef.current.requestFullscreen();
        }
    };

    // When live ends, load the full video for proper playback
    useEffect(() => {
        if (!isLive && !isBatchInitializing && isExpanded && !videoUrl && !loadingVideo) {
            // Small delay to ensure recording file is finalized
            const timer = setTimeout(() => {
                loadVideo();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isLive, isBatchInitializing]);

    const loadVideo = async () => {
        setLoadingVideo(true);
        console.log('[TestRecordingViewer] Loading video:', { recordingProjectId, recordingMessageId, filename, path: recording.path });
        try {
            const response = await api.get(
                `/browser-stream/recording/${recordingProjectId}/${recordingMessageId}/${filename}`,
                { responseType: 'blob' }
            );
            const blob = response.data as Blob;
            console.log('[TestRecordingViewer] Received blob:', { size: blob.size, type: blob.type });
            
            // Check for empty blob (video not ready yet)
            if (blob.size === 0) {
                console.warn('[TestRecordingViewer] Received empty blob, video may still be encoding');
                setError('Recording is being processed, please try again shortly');
                return;
            }
            
            const detectedMimeType = blob.type || getMimeTypeFromFilename(filename);
            setMimeType(detectedMimeType);
            
            // Get duration from blob (handles WebM without duration metadata)
            const duration = await getBlobDuration(blob);
            setVideoDuration(duration);
            console.log('[TestRecordingViewer] Video duration:', duration);
            
            const blobUrl = URL.createObjectURL(blob);
            console.log('[TestRecordingViewer] Created blob URL:', blobUrl);
            setVideoUrl(blobUrl);
            setError(null);
        } catch (err) {
            console.error('[TestRecordingViewer] Failed to load video:', err);
            setError('Failed to load recording');
        } finally {
            setLoadingVideo(false);
        }
    };

    if (error) {
        return (
            <div className={`border border-border-secondary rounded-bg-v2-md p-bg-v2-md ${className}`}>
                <div className="flex items-center gap-bg-v2-sm text-text-error-primary">
                    <Icon name={IconName.InfoCircle} size="sm" variant="error" />
                    <Text variant="text-sm" color="error">{error}</Text>
                </div>
            </div>
        );
    }

    const isActive = isLive || isBatchInitializing;

    // Strip numbering like "1.", "2)", "1:" etc. from scenario text
    const stripNumbering = (text: string): string => {
        return text.replace(/^\s*\d+[.):\-]\s*/, '').trim();
    };

    const displayScenario = currentScenario ? stripNumbering(currentScenario) : null;
    
    // Determine the title to display
    const getDisplayTitle = (): string => {
        if (batchName) {
            return batchName;
        }
        if (isBatchInitializing) {
            return 'Initializing Test';
        }
        if (isLive) {
            return 'Live Test';
        }
        return 'Test Recording';
    };

    // Check if we have test data to show in the info popover
    const hasScenarios = scenarios.length > 0;
    const hasTestPlan = testPlan.length > 0;

    return (
        <div className={`border ${isActive ? 'border-green-500/50' : 'border-border-secondary'} rounded-bg-v2-md overflow-hidden ${className}`}>
            <div
                className={`flex items-center justify-between px-bg-v2-md py-bg-v2-sm ${isActive ? 'bg-green-500/10' : 'bg-bg-secondary'} cursor-pointer`}
                onClick={() => !isActive && setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-bg-v2-sm">
                    <Icon name={IconName.VideoRecording} size="sm" variant={isActive ? "success" : "secondary"} />
                    <Text variant="text-sm" weight="medium" color="primary">
                        {getDisplayTitle()}
                    </Text>
                </div>
                <div className="flex items-center gap-bg-v2-xs">
                    {/* Info icon for test plan - only show when there is a test plan or scenarios */}
                    {(hasTestPlan || hasScenarios) && (
                        <button
                            ref={infoButtonRef}
                            onClick={handleInfoClick}
                            className="p-1 hover:bg-bg-tertiary rounded transition-colors cursor-pointer"
                            title="View test scenarios"
                        >
                            <Icon name={IconName.InfoCircle} size="sm" variant="secondary" />
                        </button>
                    )}

                    {/* Share recording - only for completed recordings that have a recordingId */}
                    {!isActive && recordingId && onShare && (
                        <button
                            onClick={handleShareClick}
                            className="p-1 hover:bg-bg-tertiary rounded transition-colors cursor-pointer"
                            title="Share recording"
                        >
                            <Icon name={IconName.Share} size="sm" variant="secondary" />
                        </button>
                    )}

                    {/* Download recording - only for completed recordings that have a recordingId */}
                    {!isActive && recordingId && (
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            className="p-1 hover:bg-bg-tertiary rounded transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            title="Download recording"
                        >
                            <Icon
                                name={downloading ? IconName.Loading : IconName.Download}
                                size="sm"
                                variant="secondary"
                                spin={downloading}
                            />
                        </button>
                    )}

                    {!isActive && (
                        <Icon
                            name={IconName.ChevronDown}
                            size="sm"
                            variant="secondary"
                            className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className="p-bg-v2-md">
                    {/* Batch initializing state */}
                    {isBatchInitializing && (
                        <div className="flex flex-col items-center justify-center py-12 bg-bg-tertiary rounded-bg-v2-sm">
                            <Icon name={IconName.Loading} size="lg" spin variant="secondary" />
                            <Text variant="text-md" weight="medium" color="primary" className="mt-4">
                                New batch of tests are initializing
                            </Text>
                        </div>
                    )}

                    {/* Main content - video only, sidebar removed */}
                    {!isBatchInitializing && (
                        <div>
                                {/* Live frame display - smooth real-time preview */}
                                {isLive && liveFrame && (
                                    <div 
                                        ref={liveContainerRef}
                                        className={`relative overflow-hidden bg-black group ${
                                            isFullscreen 
                                                ? 'fixed inset-0 z-50 flex items-center justify-center' 
                                                : 'rounded-bg-v2-sm'
                                        }`}
                                    >
                                        <img
                                            src={`data:image/jpeg;base64,${liveFrame}`}
                                            alt="Live browser view"
                                            className={`object-contain ${
                                                isFullscreen 
                                                    ? 'max-w-full max-h-full w-auto h-auto' 
                                                    : 'w-full aspect-video'
                                            }`}
                                            onDoubleClick={toggleFullscreen}
                                        />
                                        {/* Fullscreen toggle button */}
                                        <button
                                            onClick={toggleFullscreen}
                                            className={`absolute p-bg-v2-sm bg-alpha-black-50 rounded-bg-v2-sm 
                                                hover:bg-alpha-black-70 transition-colors cursor-pointer
                                                ${isFullscreen 
                                                    ? 'top-bg-v2-md right-bg-v2-md' 
                                                    : 'bottom-bg-v2-sm right-bg-v2-sm opacity-0 group-hover:opacity-100'
                                                }`}
                                            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                                        >
                                            <Icon 
                                                name={isFullscreen ? IconName.Close : IconName.Fullscreen} 
                                                size="sm" 
                                                inheritColor 
                                                className="text-fg-white" 
                                            />
                                        </button>
                                    </div>
                                )}

                                {/* Loading state when live but no frame yet */}
                                {isLive && !liveFrame && (
                                    <div className="flex items-center justify-center py-8 bg-bg-tertiary rounded-bg-v2-sm">
                                        <Icon name={IconName.Loading} size="md" spin variant="secondary" />
                                        <Text variant="text-sm" color="secondary" className="ml-2">Initializing live stream...</Text>
                                    </div>
                                )}

                                {/* Regular video loading state */}
                                {!isLive && loadingVideo && (
                                    <div className="flex items-center justify-center py-8 bg-bg-tertiary rounded-bg-v2-sm">
                                        <Icon name={IconName.Loading} size="md" spin variant="secondary" />
                                        <Text variant="text-sm" color="secondary" className="ml-2">Loading recording...</Text>
                                    </div>
                                )}
                                
                                {/* Regular video player for completed recordings */}
                                {!isLive && !loadingVideo && videoUrl && (
                                    <VideoPlayerContent
                                        videoUrl={videoUrl}
                                        mimeType={mimeType}
                                        playerRef={playerRef}
                                        duration={videoDuration}
                                    />
                                )}
                        </div>
                    )}
                </div>
            )}

            {/* Live action status text - shows agent's current action (at bottom) */}
            {isLive && currentAction && (
                <div className="px-bg-v2-md py-bg-v2-sm bg-bg-tertiary border-t border-border-secondary">
                    <Text variant="text-sm" color="secondary" className="truncate">
                        {currentAction}
                    </Text>
                </div>
            )}

            {/* Test Plan Popover Component */}
            <TestPlanPopover
                isOpen={showScenariosPopover}
                onClose={() => setShowScenariosPopover(false)}
                triggerRef={infoButtonRef}
                isLive={isLive}
                testPlan={testPlan}
                scenarios={scenarios}
                currentBatchIndex={currentBatchIndex}
            />
        </div>
    );
};

export default TestRecordingViewer;
